import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  ApiBuilder,
  AppConfigService,
  EXCEPTION_CATEGORY_ENUM,
  EXCEPTION_FILE_NAMES_ENUM,
  ExceptionTrackingService,
  HttpRequestMethods,
  IExceptionMetadata
} from '@com-delta-idp/mnx-utils';
import { Observable, forkJoin, throwError } from 'rxjs';
import { COUNTRY_CODE, IDP_APP } from '../../constants/login-constant';
import {
  AddressLinesResponse,
  CountryCodeAndContent,
  CountryListResponse,
  ValidateAddressRequest,
  ValidateAddressResponse
} from '../../models/add-address.interface';
import { LoginModalEndpointsKeys } from '../../models/login-modal.enum';
import { LoginContentService } from '../login-content.service';

@Injectable({
  providedIn: 'root'
})
export class AddAddressApiService {
  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
    private exceptionTrackingService: ExceptionTrackingService,
    private contentService: LoginContentService
  ) {}

  getCountryCodes = (): Observable<CountryListResponse> => {
    return new ApiBuilder<undefined, CountryListResponse>(
      this.http,
      this.appConfig
    )
      .app(IDP_APP)
      .endpointKey(LoginModalEndpointsKeys['COUNTRY_CODES_BFF_URL'])
      .method(HttpRequestMethods['GET'])
      .errorHandler(this.handleError.bind(this))
      .create();
  };

  getCountryCodeAndContent(): Observable<CountryCodeAndContent> {
    return forkJoin({
      countryCodes: this.getCountryCodes(),
      aemResponse: this.contentService.getAEMContent()
    });
  }

  getAddressLines = (countryCode: string): Observable<AddressLinesResponse> => {
    const queryParams = `?${COUNTRY_CODE}=${countryCode}`;
    return new ApiBuilder<undefined, AddressLinesResponse>(
      this.http,
      this.appConfig
    )
      .app(IDP_APP)
      .endpointKey(LoginModalEndpointsKeys['ADDRESS_LINES_BFF_URL'])
      .method(HttpRequestMethods['GET'])
      .queryParams(queryParams)
      .errorHandler(this.handleError.bind(this))
      .create();
  };

  validateAddress = (
    payload: ValidateAddressRequest
  ): Observable<ValidateAddressResponse> => {
    return new ApiBuilder<ValidateAddressRequest, ValidateAddressResponse>(
      this.http,
      this.appConfig
    )
      .app(IDP_APP)
      .endpointKey(LoginModalEndpointsKeys['VALIDATE_ADDRESS_BFF_URL'])
      .method(HttpRequestMethods['POST'])
      .payload(payload)
      .errorHandler(this.handleError.bind(this))
      .create();
  };

  handleError(error: HttpErrorResponse): Observable<never> {
    const exceptionMetadata: IExceptionMetadata = {
      fileName: EXCEPTION_FILE_NAMES_ENUM.ADD_ADDRESS_API_SERVICE,
      methodName: '_handleError',
      exceptionCategory: EXCEPTION_CATEGORY_ENUM.API_FAILURE,
      exceptionMessage: error.error
    };
    this.exceptionTrackingService.track(
      `[${IDP_APP}]: ${error.status} : ${error.url}`,
      exceptionMetadata
    );
    return throwError(error);
  }
}
